/**
 * CrewActivityTrackMap — the "Track" button's Leaflet map/GPS-polling/ETA-dial UI for the
 * Transport segment's Driver & Vehicle Information section, extracted out of
 * CrewActivityMobileViewComponent.jsx (which had grown to ~6800 lines) as a pure refactor —
 * no behavior change. Default-exports `TransportDetail`; `TripEtaDial`, `RouteStepsPanel` and
 * `DropInfoPanel` are internal helpers only used by TransportDetail itself.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  routeFromPayload,
  measure,
  snapToRoute,
  bearing as bearingBetween,
  km,
  minutesOf,
  connectTrip,
} from "../map/tripRoute";
import LiveTripTracking from "../map/LiveTripTracking";
import {
  SEGMENT_ACTION_NON_CLICKABLE,
  PURPLE,
  PURPLE_DARK,
  ORANGE,
  RED,
  GREY,
  GREEN_RADIANT,
  TRAFFIC_COLORS,
  trafficLevel,
  KIND_ICON,
  hasGeo,
  escapeHtml,
  avatarHtml,
  clean,
  show,
  camelKeys,
  kindOf,
  ImageSlider,
  Skeleton,
  Avatar,
  InfoTile,
  crewStatusStyle,
  verifiedStyle,
  S,
} from "./CrewActivityMobileViewComponent";

/**
 * Trip-progress half-dial for the Track map — half-circle arc filling with `prog.pct`, remaining/
 * covered distance, ETA minutes and (once a GPS fix has arrived) live speed. Same idea as
 * LiveTripMap's own dial, written fresh here with this file's own inline-style/brand-color
 * convention (PURPLE accent) rather than importing LiveTripMap's dark-glass version.
 */
const ARC_LEN = Math.PI * 68;
const dialKnob = (pct) => {
  const a = Math.PI * (1 - Math.min(1, Math.max(0, pct / 100)));
  return { x: 86 + 68 * Math.cos(a), y: 74 - 68 * Math.sin(a) };
};
/**
 * Trip-progress gauge card for the Track map — half-dial + LIVE badge, message, speed, a
 * Covered/Progress/Arrival stat row, and a whole-trip footer with route summary + GPS time +
 * altitude. Same information density as LiveTripMap's own `.ltm-gauge`, written fresh with this
 * file's inline-style/brand-color convention (PURPLE accent) rather than importing it.
 */
const TripEtaDial = ({ prog, gps, boarded, routeSummary, wholeTripText }) => {
  const knob = dialKnob(prog?.pct || 0);
  const arrival = prog?.minutes
    ? new Date(Date.now() + prog.minutes * 60000).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "--";
  return (
    <div
      style={{
        position: "absolute",
        left: 10,
        bottom: 10,
        zIndex: 500,
        width: 230,
        background: "rgba(255,255,255,0.97)",
        borderRadius: 14,
        padding: "10px 12px",
        boxShadow: "0 4px 14px rgba(40,30,70,0.22)",
        fontFamily: "Nunito,sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <span
          style={{
            font: "800 12.5px/1 Nunito,sans-serif",
            color: GREY,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {boarded ? "Drop in" : "Pickup in"}
        </span>
        {gps && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              font: "800 12px/1 Nunito,sans-serif",
              color: GREEN_RADIANT,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: GREEN_RADIANT,
                display: "inline-block",
                animation: "pulseDot 1.6s infinite",
              }}
            />
            Live
          </span>
        )}
      </div>
      <div
        style={{
          font: "800 15px/1.25 Nunito,sans-serif",
          color: "#1c1c1e",
          marginBottom: gps?.speed != null ? 1 : 6,
        }}
      >
        {prog?.message || "--"}
      </div>
      {gps?.speed != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", marginBottom: 8 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 8,
              background: "#f5f3fb",
              border: "1px solid #e6e2f2",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 10px",
                borderRadius: 999,
                background: "#0a0a0a",
                font: "800 16px/1 'JetBrains Mono',monospace",
                color: "#4da3ff",
              }}
            >
              {Math.round(gps.speed)}
            </span>
            <span style={{ font: "700 13.5px/1 Nunito,sans-serif", color: GREY }}>km/h</span>
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 12px",
              borderRadius: 999,
              font: "800 11px/1 Nunito,sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              background: gps.speed > 0 ? "rgba(34,197,94,0.14)" : "rgba(242,101,34,0.14)",
              color: gps.speed > 0 ? GREEN_RADIANT : ORANGE,
            }}
          >
            {gps.speed > 0 ? "Moving" : "Stopped"}
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="76" height="41" viewBox="0 0 172 92" aria-hidden="true" style={{ flexShrink: 0 }}>
          <path
            d="M18 74 A 68 68 0 0 1 154 74"
            fill="none"
            stroke="#e6e2f2"
            strokeWidth="10"
            strokeLinecap="round"
          />
          <path
            d="M18 74 A 68 68 0 0 1 154 74"
            fill="none"
            stroke={PURPLE}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={ARC_LEN}
            strokeDashoffset={ARC_LEN * (1 - (prog?.pct || 0) / 100)}
            style={{ transition: "stroke-dashoffset .45s ease" }}
          />
          <circle cx={knob.x} cy={knob.y} r={6} fill={PURPLE_DARK} style={{ transition: "cx .45s ease, cy .45s ease" }} />
        </svg>
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ font: "800 17px/1.1 Nunito,sans-serif", color: "#1c1c1e" }}>
            {km(prog?.remain || 0)}
          </span>
          <span style={{ font: "700 11.5px/1.2 Nunito,sans-serif", color: GREY }}>
            remaining
          </span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px solid #f0eef7",
        }}
      >
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ font: "700 11px/1.2 Nunito,sans-serif", color: GREY }}>Covered</div>
          <div style={{ font: "800 13px/1.3 Nunito,sans-serif", color: "#1c1c1e" }}>{km(prog?.covered || 0)}</div>
        </div>
        <div style={{ width: 1, height: 26, background: "#f0eef7" }} />
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ font: "700 11px/1.2 Nunito,sans-serif", color: GREY }}>Progress</div>
          <div style={{ font: "800 13px/1.3 Nunito,sans-serif", color: "#1c1c1e" }}>{prog?.pct || 0}%</div>
        </div>
        <div style={{ width: 1, height: 26, background: "#f0eef7" }} />
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ font: "700 11px/1.2 Nunito,sans-serif", color: GREY }}>Arrival</div>
          <div style={{ font: "800 13px/1.3 Nunito,sans-serif", color: "#1c1c1e" }}>{arrival}</div>
        </div>
      </div>
      {(wholeTripText || routeSummary || gps?.gpsTime) && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid #f0eef7",
            font: "700 9.5px/1.4 Nunito,sans-serif",
            color: GREY,
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {[
            wholeTripText ? `${wholeTripText} whole trip` : null,
            routeSummary ? `via ${routeSummary}` : null,
            gps?.gpsTime ? `GPS ${gps.gpsTime}` : null,
            gps?.altitude != null ? `${Math.round(gps.altitude)} m alt` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
    </div>
  );
};

/**
 * Turn-by-turn directions list for the Track map's "Route" toggle — same idea as
 * TripTrackingMap's route tab, listing `route.steps` (from tripRoute.js) with the step the
 * vehicle is currently on highlighted.
 */
const RouteStepsPanel = ({ steps, activeStep }) => (
  <div
    style={{
      position: "absolute",
      left: 10,
      bottom: 10,
      zIndex: 500,
      width: 230,
      maxHeight: 160,
      overflowY: "auto",
      background: "rgba(255,255,255,0.97)",
      borderRadius: 14,
      padding: "8px 10px",
      boxShadow: "0 4px 14px rgba(40,30,70,0.22)",
      fontFamily: "Nunito,sans-serif",
    }}
  >
    {steps.length === 0 ? (
      <div style={{ font: "700 11px/1.3 Nunito,sans-serif", color: GREY }}>
        No turn list on this route
      </div>
    ) : (
      steps.map((s, n) => (
        <div
          key={n}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "5px 4px",
            borderRadius: 8,
            background: n === activeStep ? "rgba(91,63,214,0.1)" : "transparent",
            opacity: n < activeStep ? 0.5 : 1,
          }}
        >
          <span
            style={{
              flex: "0 0 auto",
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: n === activeStep ? PURPLE : "#e6e2f2",
              color: n === activeStep ? "#fff" : GREY,
              font: "800 9px/16px Nunito,sans-serif",
              textAlign: "center",
            }}
          >
            {n + 1}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              font: "700 10.5px/1.3 Nunito,sans-serif",
              color: "#1c1c1e",
            }}
          >
            {s.text}
          </span>
          <span
            style={{
              flex: "0 0 auto",
              font: "700 9.5px/1.3 Nunito,sans-serif",
              color: GREY,
            }}
          >
            {s.distanceText}
          </span>
        </div>
      ))
    )}
  </div>
);

/**
 * Destination/drop details for the Track map's "Drop" toggle — same idea as TripTrackingMap's
 * "Drop" tab: final address, destination kind, and the route being driven to reach it.
 */
const DropInfoPanel = ({
  destination,
  destKind,
  boarded,
  routeSummary,
  routeDistanceText,
  pickupLocation,
  pickupTime,
}) => (
  <div
    style={{
      position: "absolute",
      left: 10,
      bottom: 10,
      zIndex: 500,
      width: 230,
      background: "rgba(255,255,255,0.97)",
      borderRadius: 14,
      padding: "10px 12px",
      boxShadow: "0 4px 14px rgba(40,30,70,0.22)",
      fontFamily: "Nunito,sans-serif",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}
  >
    {!boarded && pickupLocation && (
      <>
        <span
          style={{
            font: "800 10px/1 Nunito,sans-serif",
            color: GREY,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Pickup
        </span>
        <span style={{ font: "800 12px/1.35 Nunito,sans-serif", color: "#1c1c1e" }}>
          {pickupLocation}
          {pickupTime ? ` · ${pickupTime}` : ""}
        </span>
      </>
    )}
    <span
      style={{
        font: "800 10px/1 Nunito,sans-serif",
        color: GREY,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      Final drop
    </span>
    <span style={{ font: "800 12px/1.35 Nunito,sans-serif", color: "#1c1c1e" }}>
      {destination?.address || "--"}
    </span>
    <span
      style={{
        display: "inline-flex",
        alignSelf: "flex-start",
        font: "800 9.5px/1 Nunito,sans-serif",
        color: PURPLE_DARK,
        background: "#efeaff",
        borderRadius: 999,
        padding: "3px 9px",
      }}
    >
      {destKind}
    </span>
    <span style={{ font: "700 9.5px/1.4 Nunito,sans-serif", color: GREY }}>
      Routing to {boarded ? "drop" : "crew pickup"}
      {routeSummary ? ` · via ${routeSummary}` : ""}
      {routeDistanceText ? ` · ${routeDistanceText}` : ""}
    </span>
  </div>
);

const TransportDetail = ({
  activity,
  images,
  imagesLoading,
  driver,
  crews,
  eta,
  tripLoading,
  driverRefreshing,
  guard,
  guardLoading,
  tracking,
  trackingLoading,
  handlers,
  employeeNumber,
}) => {
  const [crewOpen, setCrewOpen] = useState(false);
  const [hotelFormOpen, setHotelFormOpen] = useState(false);
  const [driverOpen, setDriverOpen] = useState(false);
  const [guardOpen, setGuardOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [liveTrackOpen, setLiveTrackOpen] = useState(false);
  const [earlyFormOpen, setEarlyFormOpen] = useState(false);
  const [earlyOptions, setEarlyOptions] = useState(null);
  const [earlyChoice, setEarlyChoice] = useState(null);
  const [addressForm, setAddressForm] = useState(null); // "pickup" | "dropoff"
  const [addressList, setAddressList] = useState(null);
  const [addressChoice, setAddressChoice] = useState("");
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const routeRef = useRef(null);
  const geomRef = useRef(null);
  const headingRef = useRef(0);
  const connRef = useRef(null);
  const offRouteStrikesRef = useRef(0);
  const [gps, setGps] = useState(null);
  const [follow, setFollow] = useState(true);
  const followRef = useRef(true);
  useEffect(() => {
    followRef.current = follow;
  }, [follow]);
  // Route steps panel + spoken turn-by-turn voice guidance — same idea as TripTrackingMap's
  // ltm-segs "Route" tab and its voice toggle, added here for this map too.
  const [mapPanel, setMapPanel] = useState("eta"); // "eta" | "route"
  const [activeStep, setActiveStep] = useState(0);
  const [voiceOn, setVoiceOn] = useState(false);
  const voiceOnRef = useRef(false);
  useEffect(() => {
    voiceOnRef.current = voiceOn;
  }, [voiceOn]);
  const spokenStepRef = useRef(-1);
  const synth =
    typeof window !== "undefined" ? window.speechSynthesis : null;
  useEffect(() => () => synth?.cancel(), [synth]);
  // `handlers` is a fresh object literal on every parent render (not memoized) — reading it
  // through a ref instead of putting it in the GPS-polling effect's deps keeps that effect from
  // tearing down/restarting the connection on unrelated re-renders, only on mapOpen/activity change.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  // Leaflet map — same tiles as TrackingComponent; markers/route come from get-trip-tracking.
  // Rebuilt from scratch whenever `tracking` changes (e.g. after the Refresh pill re-fetches or
  // an off-route auto-replot lands), not just on first open. Live vehicle position/heading is
  // NOT part of this effect's deps (see the GPS-polling effect + updateVehicleMarker below) —
  // rebuilding the whole map on every ~10s GPS tick would flicker/reset pan+zoom, so only the
  // vehicle marker itself is mutated imperatively on each GPS fix.
  useEffect(() => {
    const L = window.L;
    if (!mapOpen || !mapRef.current || !L) {
      if (mapObj.current) {
        mapObj.current.remove();
        mapObj.current = null;
      }
      vehicleMarkerRef.current = null;
      return undefined;
    }
    if (mapObj.current) {
      mapObj.current.remove();
      mapObj.current = null;
    }
    const pin = (bg, label, sub, ring) =>
      L.divIcon({
        className: "",
        iconSize: null,
        iconAnchor: [14, 14],
        html:
          '<div style="display:flex;align-items:center;gap:6px;white-space:nowrap;width:max-content;">' +
          '<span style="flex:0 0 auto;width:28px;height:28px;box-sizing:border-box;border-radius:50%;background:' +
          bg +
          ";color:#fff;display:flex;align-items:center;justify-content:center;font:800 12px/1 Nunito,sans-serif;" +
          "border:" +
          (ring ? "3px solid #F5C400" : "2px solid #fff") +
          ";box-shadow:0 2px 6px rgba(40,30,70,0.28)" +
          (ring ? ",0 0 0 2px rgba(245,196,0,0.55)" : "") +
          ';">' +
          label +
          "</span>" +
          '<span style="flex:0 0 auto;background:rgba(255,255,255,0.94);border-radius:6px;padding:3px 8px;' +
          'font:800 10.5px/1.2 Nunito,sans-serif;color:#1c1c1e;">' +
          sub +
          "</span>" +
          "</div>",
      });

    const driverInfo = tracking?.driverInfo || null;
    const passengers = tracking?.passengers || [];
    const destination = tracking?.destination || null;
    const currentPassenger = passengers.find((p) => p.isCurrentPassenger) || null;
    // Single current-leg ETA/ETD message (replaces the old per-passenger get-trip-driver-eta
    // text) — matches how TripTrackingMap/LiveTripMap surface exactly one live ETA.
    const etaMessage = tracking?.eta?.trackingMessage || null;

    // Real road-following route (tripRoute.js decodes route.mapJson / route.wayPoints).
    const route = routeFromPayload(tracking);
    routeRef.current = route;
    geomRef.current = route?.points?.length ? measure(route.points) : null;

    const initialGps = gps;
    const veh =
      initialGps && hasGeo(initialGps.latitude, initialGps.longitude)
        ? [initialGps.latitude, initialGps.longitude]
        : driverInfo && hasGeo(driverInfo.vehicleLatitude, driverInfo.vehicleLongitude)
          ? [driverInfo.vehicleLatitude, driverInfo.vehicleLongitude]
          : null;
    const dest =
      destination && hasGeo(destination.latitude, destination.longitude)
        ? [destination.latitude, destination.longitude]
        : null;
    const map = L.map(mapRef.current, {
      center: veh || dest || [0, 0],
      zoom: veh || dest ? 13 : 2,
    });
    // TripEtaDial/RouteStepsPanel/DropInfoPanel all anchor bottom-left — move the
    // zoom control out of that corner (and away from the top-left "DROP IN" card)
    // so it never overlaps them, matching LiveTripMap.jsx's own convention.
    map.zoomControl.setPosition("bottomright");
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    const pts = [];
    if (veh && driverInfo) {
      const driverPopup =
        '<div style="display:flex;align-items:center;gap:8px;max-width:220px;">' +
        (driverInfo.driverPhotoUrl
          ? '<img src="' +
            escapeHtml(driverInfo.driverPhotoUrl) +
            '" style="width:38px;height:38px;border-radius:50%;object-fit:cover;flex:0 0 auto;" />'
          : "") +
        '<div style="min-width:0;">' +
        (driverInfo.driverName
          ? "<div><b>" + escapeHtml(driverInfo.driverName) + "</b></div>"
          : "") +
        (driverInfo.driverMobileNo
          ? '<div><a href="tel:' +
            escapeHtml(driverInfo.driverMobileNo) +
            '">' +
            escapeHtml(driverInfo.driverMobileNo) +
            "</a></div>"
          : "") +
        (etaMessage
          ? '<div style="margin-top:2px;font-weight:800;color:' +
            ORANGE +
            ';">' +
            escapeHtml(etaMessage) +
            "</div>"
          : "") +
        "</div></div>";
      // A dedicated, fixed-size car-icon pin sits exactly on the vehicle's real lat/lng — its own
      // 32x32 box is anchored dead-center, unaffected by the info card's variable-width content.
      // The driver/vehicle info card is a sibling, absolutely positioned beside the pin, so it
      // never shifts the anchor point. ".veh-rot" wraps just the circle so live GPS ticks can
      // rotate the car to match travel bearing without touching the card beside it.
      const VEHICLE_PIN_SIZE = 32;
      const vehicleIcon = L.divIcon({
        className: "",
        iconSize: null,
        iconAnchor: [VEHICLE_PIN_SIZE / 2, VEHICLE_PIN_SIZE / 2],
        html:
          '<div style="position:relative;width:' +
          VEHICLE_PIN_SIZE +
          "px;height:" +
          VEHICLE_PIN_SIZE +
          'px;">' +
          '<div class="veh-rot" style="width:' +
          VEHICLE_PIN_SIZE +
          "px;height:" +
          VEHICLE_PIN_SIZE +
          "px;border-radius:50%;background:" +
          PURPLE +
          ';color:#fff;display:flex;align-items:center;justify-content:center;' +
          'border:2px solid #fff;box-shadow:0 2px 6px rgba(40,30,70,0.28);' +
          'transform-origin:50% 50%;transition:transform 0.4s linear;">' +
          '<i class="fas fa-car-side" style="font-size:14px;"></i>' +
          "</div>" +
          '<div style="position:absolute;left:' +
          (VEHICLE_PIN_SIZE + 6) +
          'px;top:0;display:flex;flex-direction:column;gap:3px;' +
          'width:max-content;max-width:230px;background:rgba(255,255,255,0.95);border-radius:10px;' +
          'padding:6px 10px;box-shadow:0 2px 6px rgba(40,30,70,0.28);">' +
          (driverInfo.driverName || driverInfo.driverMobileNo
            ? '<div style="display:flex;align-items:center;gap:6px;">' +
              '<div style="width:22px;height:22px;border-radius:50%;box-sizing:border-box;border:2px solid ' +
              PURPLE +
              ';display:flex;align-items:center;justify-content:center;overflow:hidden;background:#fff;flex:0 0 auto;">' +
              avatarHtml(driverInfo.driverPhotoUrl, driverInfo.driverName, 16) +
              "</div>" +
              '<div style="display:flex;flex-direction:column;gap:0;min-width:0;">' +
              (driverInfo.driverName
                ? '<span style="font:800 10.5px/1.2 Nunito,sans-serif;color:#1c1c1e;white-space:nowrap;' +
                  'overflow:hidden;text-overflow:ellipsis;">' +
                  escapeHtml(driverInfo.driverName) +
                  "</span>"
                : "") +
              (driverInfo.driverMobileNo
                ? '<span style="font:700 9.5px/1.2 Nunito,sans-serif;color:#5a5a63;white-space:nowrap;' +
                  'overflow:hidden;text-overflow:ellipsis;">' +
                  escapeHtml(driverInfo.driverMobileNo) +
                  "</span>"
                : "") +
              "</div></div>"
            : "") +
          '<span style="font:800 10.5px/1.2 Nunito,sans-serif;color:#1c1c1e;white-space:nowrap;' +
          'overflow:hidden;text-overflow:ellipsis;">' +
          escapeHtml(driverInfo.vehicleNo || "Vehicle") +
          "</span>" +
          (driverInfo.location
            ? '<span style="font:700 9.5px/1.2 Nunito,sans-serif;color:#5a5a63;white-space:nowrap;' +
              'overflow:hidden;text-overflow:ellipsis;">' +
              escapeHtml(driverInfo.location) +
              "</span>"
            : "") +
          (etaMessage
            ? '<span style="font:800 9.5px/1.2 Nunito,sans-serif;color:' +
              ORANGE +
              ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' +
              escapeHtml(etaMessage) +
              "</span>"
            : "") +
          "</div>" +
          "</div>",
      });
      const vehicleMarker = L.marker(veh, { icon: vehicleIcon })
        .addTo(map)
        .bindPopup(driverPopup);
      vehicleMarkerRef.current = vehicleMarker;
      pts.push(veh);
    } else {
      vehicleMarkerRef.current = null;
    }
    (passengers || [])
      .slice()
      .sort((x, y) => (x.srNo || 0) - (y.srNo || 0))
      .forEach((p) => {
        // Boarded crews are no longer plotted individually — they only show up as a count on
        // the vehicle marker (see boardedPassengers below); the current passenger's ETA lives
        // on the vehicle popup/card as the single current-leg message (see etaMessage above).
        if (p.isBoarded !== false) return;
        if (!hasGeo(p.pickupLatitude, p.pickupLongitude)) return;
        const ll = [p.pickupLatitude, p.pickupLongitude];
        const ring = p.isCurrentPassenger ? ORANGE : "#c4c2ce";
        const nameLine =
          escapeHtml(p.crewName || "") +
          (p.airlineCrewType ? " \u00b7 " + escapeHtml(p.airlineCrewType) : "");
        // Same structure as the vehicle marker: a fixed-size pin sits exactly on the pickup
        // point (own 32x32 box, anchored dead-center), with the name/location card as a sibling
        // absolutely positioned beside it — so the card never shifts or overlaps the actual pin.
        const PAX_PIN_SIZE = 32;
        const passengerIcon = L.divIcon({
          className: "",
          iconSize: null,
          iconAnchor: [PAX_PIN_SIZE / 2, PAX_PIN_SIZE / 2],
          html:
            '<div style="position:relative;width:' +
            PAX_PIN_SIZE +
            "px;height:" +
            PAX_PIN_SIZE +
            'px;">' +
            '<div style="width:' +
            PAX_PIN_SIZE +
            "px;height:" +
            PAX_PIN_SIZE +
            "px;border-radius:50%;box-sizing:border-box;border:" +
            (p.isCurrentPassenger ? "3px" : "2px") +
            " solid " +
            ring +
            ";display:flex;align-items:center;justify-content:center;overflow:hidden;" +
            'background:#fff;box-shadow:0 2px 6px rgba(40,30,70,0.28);">' +
            avatarHtml(p.crewProfilePhotoUrl, p.crewName, 26) +
            "</div>" +
            '<div style="position:absolute;left:' +
            (PAX_PIN_SIZE + 6) +
            'px;top:0;display:flex;flex-direction:column;gap:1px;' +
            'width:max-content;max-width:200px;background:rgba(255,255,255,0.95);border-radius:10px;' +
            'padding:4px 10px;box-shadow:0 2px 6px rgba(40,30,70,0.28);">' +
            '<span style="font:800 10.5px/1.2 Nunito,sans-serif;color:#1c1c1e;white-space:nowrap;' +
            'overflow:hidden;text-overflow:ellipsis;">' +
            nameLine +
            "</span>" +
            (p.pickupLocation
              ? '<span style="font:700 9.5px/1.2 Nunito,sans-serif;color:#5a5a63;white-space:nowrap;' +
                'overflow:hidden;text-overflow:ellipsis;">' +
                escapeHtml(p.pickupLocation) +
                "</span>"
              : "") +
            "</div></div>",
        });
        const marker = L.marker(ll, { icon: passengerIcon })
          .addTo(map)
          .bindPopup(
            [
              avatarHtml(p.crewProfilePhotoUrl, p.crewName, 36),
              p.crewName,
              p.employeeNumber,
              p.airlineCrewType,
              p.tripStatus,
              p.crewMobileNo
                ? '<a href="tel:' +
                  escapeHtml(p.crewMobileNo) +
                  '">' +
                  escapeHtml(p.crewMobileNo) +
                  "</a>"
                : null,
              p.pickupLocation,
              p.pickupTime,
            ]
              .filter(Boolean)
              .join("<br/>"),
          );
        if (p.isCurrentPassenger) marker.setZIndexOffset(1000);
        pts.push(ll);
      });
    const boardedPassengers = (passengers || []).filter((p) => p.isBoarded);
    if (veh && boardedPassengers.length > 0) {
      // Vehicle marker's pin+card assembly is top-aligned (see vehicleIcon above), with the card's
      // own left edge starting 38px right of the pin's anchor and the assembly's top edge 16px
      // above the vehicle's geo point. Anchor this badge to sit snugly just above that top edge,
      // left-aligned with the info card rather than the pin.
      const boardedIcon = L.divIcon({
        className: "",
        iconSize: null,
        iconAnchor: [-22, 40],
        html:
          '<div style="display:inline-flex;align-items:center;gap:5px;white-space:nowrap;' +
          'background:#1e7a45;color:#fff;border-radius:999px;padding:4px 10px;' +
          'font:800 11px/1 Nunito,sans-serif;border:2px solid #fff;box-shadow:0 2px 6px rgba(40,30,70,0.28);">' +
          '<i class="fas fa-user-check" style="font-size:10px;"></i>' +
          boardedPassengers.length +
          " boarded</div>",
      });
      const boardedPopup = boardedPassengers
        .map(
          (b) =>
            "<div><b>" +
            escapeHtml(b.crewName || "") +
            "</b><br/>Boarded " +
            escapeHtml(b.boardedTime || "") +
            "<br/>" +
            escapeHtml(b.pickupLocation || "") +
            "</div>",
        )
        .join('<hr style="margin:6px 0;border-color:#eee;" />');
      L.marker(veh, { icon: boardedIcon }).addTo(map).bindPopup(boardedPopup);
    }
    if (dest) {
      const icon = destination.isAirport
        ? "fas fa-plane"
        : destination.isHotel
          ? "fas fa-bed"
          : destination.isResidence
            ? "fas fa-home"
            : destination.isGroundDuty
              ? "fas fa-briefcase"
              : "fas fa-map-marker-alt";
      // 100m geofence around the destination. A real-world L.circle shrinks to invisible once the
      // map is zoomed out to fit the whole route, so it's drawn as a fixed-pixel circleMarker instead —
      // always a clearly visible dotted highlight ring around the pin, at any zoom level.
      L.circleMarker(dest, {
        radius: 26,
        color: PURPLE_DARK,
        weight: 2.5,
        dashArray: "4,7",
        fill: true,
        fillColor: PURPLE_DARK,
        fillOpacity: 0.1,
      }).addTo(map);
      L.marker(dest, {
        icon: pin(
          PURPLE_DARK,
          `<i class="${icon}"></i>`,
          destination.address || "Destination",
        ),
      })
        .addTo(map)
        .bindPopup(escapeHtml(destination.address || "Destination"));
      pts.push(dest);
    }
    // Prefer the real road-following route; fall back to the straight line through
    // vehicle/passengers/destination when this get-trip-tracking response has no route field.
    // Same 3-layer look as TripTrackingMap/LiveTripMap: a soft wide glow, a semi-opaque core,
    // and a thin bright line with an animated dashed "flow" highlight (see .ca-route-flow /
    // @keyframes caRouteFlow in this file's global <style> block).
    // Whole-route congestion color (route.durationValue vs route.trafficValue) — no per-segment
    // traffic data exists (traffic_speed_entry is always empty), so the entire glow+core pair
    // is tinted by one ratio, same as LiveTripTracking.jsx. The thin flow line stays fixed white.
    const routeTrafficRatio = route?.durationValue && route?.trafficValue
      ? route.trafficValue / route.durationValue
      : null;
    const routeColor = routeTrafficRatio != null
      ? TRAFFIC_COLORS[trafficLevel(routeTrafficRatio)]
      : GREEN_RADIANT;
    const drawRouteLine = (linePts) => {
      L.polyline(linePts, {
        color: routeColor,
        weight: 15,
        opacity: 0.13,
        lineCap: "round",
        interactive: false,
      }).addTo(map);
      L.polyline(linePts, {
        color: routeColor,
        weight: 5,
        opacity: 0.4,
        lineCap: "round",
      }).addTo(map);
      L.polyline(linePts, {
        color: "#eafff6",
        weight: 2.4,
        opacity: 0.9,
        className: "ca-route-flow",
        interactive: false,
      }).addTo(map);
    };
    const routePts = route?.points?.length > 1 ? route.points : null;
    if (routePts) {
      drawRouteLine(routePts);
      map.fitBounds(routePts, { padding: [36, 36] });
    } else if (pts.length > 1) {
      drawRouteLine(pts);
      map.fitBounds(pts, { padding: [36, 36] });
    }
    mapObj.current = map;
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
    // `gps` deliberately excluded below: only used to seed the initial vehicle position, must
    // not trigger a full map rebuild on every GPS tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOpen, tracking]);

  // Live vehicle marker update — moves/rotates the existing marker in place (no map rebuild).
  // Prefers the GPS bearing; at a standstill it's meaningless, so the last heading holds, and it
  // falls back to the route direction when the field is absent — same rule LiveTripMap uses.
  // Reads `followRef` (not the `follow` state) so this stays correct even though the GPS-polling
  // effect below deliberately doesn't recreate its connection when this function's identity
  // changes — the ref makes the Follow toggle take effect on the very next tick either way.
  const updateVehicleMarker = useCallback((fix) => {
    const marker = vehicleMarkerRef.current;
    if (!marker) return;
    const latlng = [fix.latitude, fix.longitude];
    marker.setLatLng(latlng);
    const route = routeRef.current;
    const moving = fix.speed != null && fix.speed > 1;
    let heading =
      fix.bearing != null && (moving || fix.bearing > 0) ? fix.bearing : null;
    if (heading == null && moving && route?.points?.length > 1) {
      const { index } = snapToRoute(route.points, fix.latitude, fix.longitude);
      const next = route.points[Math.min(index + 1, route.points.length - 1)];
      heading = bearingBetween(latlng, next);
    }
    if (heading != null) headingRef.current = heading;
    const el = marker.getElement()?.querySelector(".veh-rot");
    if (el) el.style.transform = `rotate(${headingRef.current}deg)`;
    if (followRef.current && mapObj.current) {
      mapObj.current.panTo(latlng, { animate: true });
    }
  }, []);

  // Live GPS polling while the map is open — getTripTracking (route/passengers/destination)
  // stays manual (Track click / Refresh pill), getVehicleGpsLocation polls every 10s to move the
  // vehicle marker. Reuses tripRoute.js's connectTrip exactly as LiveTripMap does; `onTracking`
  // is omitted since `handlers.onLoadTripTracking` already updates parent state itself.
  useEffect(() => {
    if (!mapOpen) {
      if (connRef.current) connRef.current.stop();
      connRef.current = null;
      setGps(null);
      offRouteStrikesRef.current = 0;
      setMapPanel("eta");
      setActiveStep(0);
      spokenStepRef.current = -1;
      if (synth) synth.cancel();
      return undefined;
    }
    const hasGps = !!handlersRef.current.onFetchVehicleGpsLocation;
    const conn = connectTrip({
      getTracking: () => handlersRef.current.onLoadTripTracking?.(activity),
      getGps: hasGps
        ? () => handlersRef.current.onFetchVehicleGpsLocation(activity)
        : undefined,
      gpsIntervalMs: 10000,
      onGps: (p) => {
        if (p.latitude == null || p.longitude == null) return;
        const fix = {
          latitude: +p.latitude,
          longitude: +p.longitude,
          speed: p.speed != null ? +p.speed : null,
          bearing: p.bearing != null ? +p.bearing : null,
          altitude: p.altitude != null ? +p.altitude : null,
          gpsTime: p.gpsTime || "",
        };
        setGps(fix);
        updateVehicleMarker(fix);
        // Server says the vehicle has moved >= 50m since the last comparison — reload the
        // route and/or refresh the ETA, independent of (and in addition to) the off-route
        // check below.
        if (p.isReloadMap) {
          conn.replot();
        }
        const route = routeRef.current;
        const geom = geomRef.current;
        if (route?.points?.length > 1) {
          const { index, offRouteMetres } = snapToRoute(
            route.points,
            fix.latitude,
            fix.longitude,
          );
          // Off-route for two GPS fixes running → the plotted route is stale, refetch it.
          if (offRouteMetres > 150) {
            offRouteStrikesRef.current += 1;
            if (offRouteStrikesRef.current >= 2) {
              offRouteStrikesRef.current = 0;
              conn.replot();
            }
          } else {
            offRouteStrikesRef.current = 0;
          }
          // Turn-by-turn step tracking + spoken voice guidance — same idea as
          // TripTrackingMap's own "Route" tab + voice toggle.
          if (route.steps?.length && geom) {
            let si = 0;
            route.steps.forEach((s, n) => {
              const sAt = snapToRoute(route.points, s.at[0], s.at[1]).index;
              if (geom.cum[index] >= geom.cum[sAt]) si = n;
            });
            setActiveStep(si);
            if (voiceOnRef.current && synth && si !== spokenStepRef.current) {
              const cur = route.steps[si];
              const after = route.steps[si + 1];
              if (cur && (spokenStepRef.current !== -1 || si > 0)) {
                const u = new SpeechSynthesisUtterance(
                  after
                    ? `${cur.text}. Then in ${cur.distanceText}, ${after.text.toLowerCase()}.`
                    : `${cur.text}. Arriving now.`,
                );
                u.lang = "en-IN";
                synth.speak(u);
              }
              spokenStepRef.current = si;
            }
          }
        }
      },
      onError: (err) => console.log(err),
    });
    connRef.current = conn;
    return () => conn.stop();
    // `updateVehicleMarker` is a stable (empty-deps) callback; `handlers`/`handlersRef`
    // deliberately excluded below — read via ref (see handlersRef above) precisely so this
    // effect only restarts on mapOpen/activity, not on every parent re-render (handlers is a
    // fresh object literal each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOpen, activity]);

  // Current-leg trip progress (remaining/covered distance, %, ETA minutes) for the dial — mirrors
  // LiveTripMap's own `prog` useMemo, driven by the live `gps` fix once one has arrived.
  const boarded = !!(
    tracking?.eta?.isBoarded ||
    (tracking?.passengers || []).find((p) => p.isCurrentPassenger)?.isBoarded
  );
  const prog = useMemo(() => {
    const e = tracking?.eta || {};
    const geom = geomRef.current;
    const total = e.distanceValue || geom?.total || 0;
    let remain = e.distanceValue != null ? e.distanceValue : total;
    const route = routeRef.current;
    if (gps && route?.points?.length > 1 && geom) {
      const { index } = snapToRoute(route.points, gps.latitude, gps.longitude);
      remain = Math.max(0, geom.total - geom.cum[index]);
    }
    const legMin = e.durationValue || minutesOf(e.durationText);
    const minutes = Math.min(legMin || 0, Math.max(0, Math.round(legMin * (remain / (total || 1)))));
    return {
      total,
      remain,
      covered: Math.max(0, total - remain),
      pct: total ? Math.min(100, Math.max(0, Math.round((1 - remain / total) * 100))) : 0,
      minutes,
      message:
        gps == null && e.trackingMessage
          ? e.trackingMessage
          : `${boarded ? "Drop" : "Pickup"} in ${minutes < 1 ? "<1" : minutes} min${minutes === 1 ? "" : "s"}`,
    };
  }, [tracking, gps, boarded]);

  const [hotelChoice, setHotelChoice] = useState("");
  const kind = kindOf(activity);
  // Take whichever detail object the payload actually carries, so a mis-resolved
  // kind can never blank the view. PascalCase responses are covered too.
  const rawDetail =
    activity.localTransportDetails ??
    activity.hotelTransportDetails ??
    activity.LocalTransportDetails ??
    activity.HotelTransportDetails ??
    null;
  const d = camelKeys(rawDetail || {});
  const liveTrackTransactionId = d.transactionId ?? activity.transactionId;
  const liveTrackAirlineCode =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("session_airlineCode")
      : "";
  const isShuttleSegment =
    activity.showHotelTransport === true ||
    activity.ShowHotelTransport === true;
  const live = activity.isTransportCancelled !== true;
  const allowCancel = live && d.allowCancelTransport === true;
  const allowReady = live && d.allowReadyPickupRequest === true;
  const allowEarly = live && d.allowEarlyPickup === true;
  const canCancel = allowCancel && !SEGMENT_ACTION_NON_CLICKABLE;
  const canReady = allowReady && !SEGMENT_ACTION_NON_CLICKABLE;
  const canEarly = allowEarly && !SEGMENT_ACTION_NON_CLICKABLE;
  const isShuttle = isShuttleSegment || kind === "hotelTransport";
  const allowChangeHotel = isShuttle && live && d.allowChangeHotel === true;
  const canChangeHotel = allowChangeHotel && !SEGMENT_ACTION_NON_CLICKABLE;
  const trackHasGeo =
    hasGeo(
      tracking?.driverInfo?.vehicleLatitude,
      tracking?.driverInfo?.vehicleLongitude,
    ) ||
    (tracking?.passengers || []).some((p) =>
      hasGeo(p.pickupLatitude, p.pickupLongitude),
    ) ||
    hasGeo(tracking?.destination?.latitude, tracking?.destination?.longitude);

  const actionStyle = (enabled, primary) => ({
    minHeight: 38,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: enabled ? "pointer" : "default",
    border: `1px solid ${!enabled ? "rgba(0,0,0,0.06)" : primary ? PURPLE_DARK : RED}`,
    background: !enabled ? "rgba(0,0,0,0.04)" : primary ? PURPLE_DARK : "#fff",
    color: !enabled ? "#a99483" : primary ? "#fff" : RED,
  });

  return (
    <>
      <ImageSlider images={images} loading={imagesLoading} kind={kind} />

      <div
        style={{
          background: ORANGE,
          borderRadius: 8,
          minHeight: 34,
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "5px 11px",
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.28)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className={KIND_ICON[kind]} style={{ fontSize: 12 }} />
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.25,
          }}
        >
          {clean(isShuttle ? d.hotelName : d.transporterName) ||
            show(activity.dashboardActivityText)}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(" +
            (isShuttle && d.estimatedPickupTime
              ? d.allowChangeHotel === true && live
                ? 3
                : 2
              : 2) +
            ", minmax(0, 1fr))",
          alignItems: "stretch",
          gap: 8,
        }}
      >
        {isShuttle && d.estimatedPickupTime && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #f0eff5",
              borderRadius: 8,
              padding: "4px 12px",
              display: "flex",
              gap: 10,
              alignItems: "center",
              minHeight: 38,
              boxSizing: "border-box",
            }}
          >
            <span style={{ ...S.iconCircle, width: 28, height: 28 }}>
              <i className="far fa-clock" style={{ fontSize: 12 }} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: GREY, fontWeight: 600 }}>
                Estimated Pick Up Time
              </div>
              <div style={{ fontSize: 13, color: ORANGE, fontWeight: 800 }}>
                {show(d.estimatedPickupTime)}
              </div>
            </div>
          </div>
        )}
        <button
          type="button"
          style={actionStyle(canCancel, false)}
          disabled={!canCancel}
          onClick={() =>
            canCancel &&
            handlers.onCancelRide &&
            handlers.onCancelRide(activity)
          }
        >
          <i className="fas fa-times" style={{ fontSize: 12 }} />
          {isShuttle ? "Cancel Shuttle" : "Cancel Ride"}
        </button>
        {allowChangeHotel && (
          <button
            type="button"
            style={actionStyle(canChangeHotel, true)}
            disabled={!canChangeHotel}
            onClick={() => {
              if (!canChangeHotel) return;
              setHotelChoice("");
              setHotelFormOpen(true);
            }}
          >
            <i className="fas fa-hotel" style={{ fontSize: 12 }} />
            Change Hotel
          </button>
        )}
        {!isShuttle && (
          <button
            type="button"
            style={actionStyle(canReady, true)}
            disabled={!canReady}
            onClick={() =>
              canReady &&
              handlers.onReadyForPickup &&
              handlers.onReadyForPickup(activity)
            }
          >
            <i className="fas fa-hand-paper" style={{ fontSize: 12 }} />
            I&rsquo;m Ready
          </button>
        )}
      </div>

      {/* transportCancellationNote — always visible */}
      {(d.transportCancellationNote || d.transportCancelNote) && (
        <div style={S.note}>
          <i
            className="fas fa-info-circle"
            style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
            {d.transportCancellationNote || d.transportCancelNote}
          </span>
        </div>
      )}
      {isShuttle && hotelFormOpen && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e6dffb",
            borderRadius: 10,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 7,
                background: "rgba(91,63,214,0.10)",
                color: PURPLE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fas fa-hotel" style={{ fontSize: 11 }} />
            </span>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: "#1c1c1e" }}>
              Change Hotel
            </span>
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: GREY }}>
              Select hotel
            </span>
            <select
              value={hotelChoice}
              onChange={(e) => setHotelChoice(e.target.value)}
              style={{
                height: 38,
                borderRadius: 8,
                border: "1px solid #dcd6ef",
                background: "#fff",
                color: "#1c1c1e",
                fontSize: 13,
                fontWeight: 700,
                padding: "0 10px",
                fontFamily: "inherit",
              }}
            >
              <option value="">Choose a hotel</option>
              {(d.hotelList || []).map((h) => (
                <option key={h.hotelId} value={h.hotelId}>
                  {h.hotelName}
                </option>
              ))}
            </select>
          </label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <button
              type="button"
              style={actionStyle(true, true)}
              onClick={() => {
                if (handlers.onChangeHotel)
                  handlers.onChangeHotel(activity, hotelChoice);
                setHotelFormOpen(false);
                setHotelChoice("");
              }}
            >
              <i className="fas fa-check" style={{ fontSize: 11 }} />
              Submit
            </button>
            <button
              type="button"
              style={{
                ...actionStyle(true, false),
                border: "1px solid #dcd6ef",
                color: PURPLE,
              }}
              onClick={() => {
                setHotelFormOpen(false);
                setHotelChoice("");
              }}
            >
              <i className="fas fa-times" style={{ fontSize: 11 }} />
              Cancel
            </button>
          </div>
        </div>
      )}
      {isShuttle &&
        live &&
        d.allowChangeHotel !== true &&
        d.hotelChangeNote && (
          <div style={S.note}>
            <i
              className="fas fa-info-circle"
              style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
            />
            <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
              {d.hotelChangeNote}
            </span>
          </div>
        )}
      {!isShuttle &&
        live &&
        !d.allowReadyPickupRequest &&
        d.readyPickupNote && (
          <div style={S.note}>
            <i
              className="fas fa-info-circle"
              style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
            />
            <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
              {d.readyPickupNote}
            </span>
          </div>
        )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
        {!isShuttle && d.estimatedPickupTime && (
          <div style={S.infoCard}>
            <span style={S.iconCircle}>
              <i className="far fa-clock" style={{ fontSize: 13 }} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={S.label}>Estimated Pick Up Time</div>
              <div style={{ ...S.value, color: ORANGE }}>
                {show(d.estimatedPickupTime)}
              </div>
            </div>
            <button
              type="button"
              disabled={!canEarly}
              onClick={() => {
                if (!canEarly) return;
                setEarlyChoice(null);
                setEarlyOptions(null);
                setEarlyFormOpen(true);
                if (handlers.onFetchEarlyPickupTimes) {
                  Promise.resolve(
                    handlers.onFetchEarlyPickupTimes(activity),
                  ).then((list) =>
                    setEarlyOptions(Array.isArray(list) ? list : []),
                  );
                }
              }}
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 32,
                padding: "0 12px",
                borderRadius: 8,
                border: "none",
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 800,
                whiteSpace: "nowrap",
                cursor: canEarly ? "pointer" : "default",
                background: canEarly ? RED : "rgba(0,0,0,0.04)",
                color: canEarly ? "#fff" : "#a99483",
              }}
            >
              <i className="far fa-clock" style={{ fontSize: 11 }} />
              Early Pickup
            </button>
          </div>
        )}

        {earlyFormOpen && (
          <div style={S.formCard}>
            <div style={S.formHead}>
              <span style={S.formHeadIcon}>
                <i className="far fa-clock" style={{ fontSize: 11 }} />
              </span>
              <span style={S.formHeadText}>Early Pickup</span>
            </div>
            {!earlyOptions ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Skeleton height={9} width={110} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                </div>
              </div>
            ) : (
              <>
                <span style={S.formLabel}>Select early pickup time</span>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 8,
                  }}
                >
                  {earlyOptions.map((o) => {
                    const picked = earlyChoice === o.id;
                    return (
                      <span
                        key={o.id}
                        onClick={() => setEarlyChoice(o.id)}
                        style={{
                          minHeight: 40,
                          boxSizing: "border-box",
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          padding: "0 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 700,
                          background: picked ? "#EDE8FD" : "#fff",
                          border: "1px solid " + (picked ? PURPLE : "#e2e0ea"),
                          color: picked ? PURPLE_DARK : "#3c3c43",
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border:
                              "2px solid " + (picked ? PURPLE : "#c4c2ce"),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {picked && (
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: PURPLE,
                              }}
                            />
                          )}
                        </span>
                        {o.name}
                      </span>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    style={actionStyle(true, true)}
                    onClick={() => {
                      if (handlers.onEarlyPickup)
                        handlers.onEarlyPickup(activity, earlyChoice);
                      setEarlyFormOpen(false);
                      setEarlyChoice(null);
                    }}
                  >
                    <i className="fas fa-check" style={{ fontSize: 11 }} />
                    Submit
                  </button>
                  <button
                    type="button"
                    style={{
                      ...actionStyle(true, false),
                      border: "1px solid #dcd6ef",
                      color: PURPLE,
                    }}
                    onClick={() => {
                      setEarlyFormOpen(false);
                      setEarlyChoice(null);
                    }}
                  >
                    <i className="fas fa-times" style={{ fontSize: 11 }} />
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {addressForm && (
          <div style={S.formCard}>
            <div style={S.formHead}>
              <span style={S.formHeadIcon}>
                <i className="fas fa-map-marker-alt" style={{ fontSize: 11 }} />
              </span>
              <span style={S.formHeadText}>
                {addressForm === "dropoff"
                  ? "Change Drop off Location"
                  : "Change Pick Up Location"}
              </span>
            </div>
            {!addressList ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Skeleton height={9} width={100} />
                <Skeleton height={38} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  <Skeleton height={38} />
                  <Skeleton height={38} />
                </div>
              </div>
            ) : (
              <>
                <label
                  style={{ display: "flex", flexDirection: "column", gap: 5 }}
                >
                  <span style={S.formLabel}>Select address</span>
                  <select
                    value={addressChoice}
                    onChange={(e) => setAddressChoice(e.target.value)}
                    style={S.select}
                  >
                    <option value="">Choose an address</option>
                    {addressList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {[s.name, s.address].filter(Boolean).join(" \u2014 ")}
                      </option>
                    ))}
                  </select>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    style={actionStyle(true, true)}
                    onClick={() => {
                      const fn =
                        addressForm === "pickup"
                          ? handlers.onChangePickupAddress
                          : handlers.onChangeDropoffAddress;
                      if (fn) fn(activity, addressChoice);
                      setAddressForm(null);
                      setAddressChoice("");
                    }}
                  >
                    <i className="fas fa-check" style={{ fontSize: 11 }} />
                    Submit
                  </button>
                  <button
                    type="button"
                    style={{
                      ...actionStyle(true, false),
                      border: "1px solid #dcd6ef",
                      color: PURPLE,
                    }}
                    onClick={() => {
                      setAddressForm(null);
                      setAddressChoice("");
                    }}
                  >
                    <i className="fas fa-times" style={{ fontSize: 11 }} />
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {["pickup", "dropoff"].map((which) => {
          const value =
            which === "pickup" ? d.pickupLocation : d.dropoffLocation;
          if (!value || !String(value).trim()) return null; // hide section when no address
          const address =
            which === "pickup" ? d.pickupAddress : d.dropoffAddress;
          const allowed =
            !isShuttle &&
            (which === "pickup"
              ? d.allowChangePickupAddress === true
              : d.allowChangeDropoffAddress === true);
          const canClick = allowed && !SEGMENT_ACTION_NON_CLICKABLE;
          const onChange =
            which === "pickup"
              ? handlers.onChangePickupAddress
              : handlers.onChangeDropoffAddress;
          return (
            <div
              key={which}
              style={{ ...S.infoCard, alignItems: "flex-start" }}
            >
              <span style={S.iconCircle}>
                <i
                  className={
                    which === "pickup" ? "fas fa-map-marker-alt" : "fas fa-flag"
                  }
                  style={{ fontSize: 13 }}
                />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={S.label}>
                  {which === "pickup"
                    ? "Pick Up Location"
                    : "Drop off Location"}
                </div>
                <div style={S.value}>{value}</div>
                {address && String(address).trim() && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6a6a75",
                      fontWeight: 600,
                      lineHeight: 1.45,
                      marginTop: 2,
                    }}
                  >
                    {address}
                  </div>
                )}
                {!isShuttle && live && !allowed && d.changeAddressNote && (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginTop: 5,
                      alignItems: "flex-start",
                    }}
                  >
                    <i
                      className="fas fa-info-circle"
                      style={{ color: PURPLE, fontSize: 11, marginTop: 2 }}
                    />
                    <span
                      style={{
                        fontSize: 11.5,
                        color: "#6a6a75",
                        lineHeight: 1.4,
                      }}
                    >
                      {d.changeAddressNote}
                    </span>
                  </div>
                )}
              </div>
              {live && allowed && (
                <button
                  type="button"
                  disabled={!canClick}
                  onClick={() => {
                    if (!canClick) return;
                    setAddressChoice("");
                    setAddressList(null);
                    setAddressForm(which);
                    if (handlers.onFetchStationAddress) {
                      Promise.resolve(
                        handlers.onFetchStationAddress(activity, which),
                      ).then((list) =>
                        setAddressList(Array.isArray(list) ? list : []),
                      );
                    }
                  }}
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    height: 30,
                    padding: "0 11px",
                    borderRadius: 8,
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    cursor: canClick ? "pointer" : "not-allowed",
                    border: `1px solid ${canClick ? PURPLE : "#c9c9d1"}`,
                    background: canClick ? "#fff" : "#f2f2f5",
                    color: canClick ? PURPLE : "#9a9aa5",
                    opacity: canClick ? 1 : 0.7,
                  }}
                >
                  <i className="fas fa-pen" style={{ fontSize: 10 }} />
                  Change
                </button>
              )}
            </div>
          );
        })}

        {d.transporterPhoneNumber && (
          <div style={S.infoCard}>
            <span style={S.iconCircle}>
              <i className="fas fa-phone-alt" style={{ fontSize: 13 }} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={S.label}>Transporter Company Phone</div>
              <div style={{ ...S.value, color: ORANGE }}>
                {show(d.transporterPhoneNumber)}
              </div>
            </div>
            <a
              href={`tel:${d.transporterPhoneNumber}`}
              style={{ marginLeft: "auto" }}
            >
              <i
                className="fas fa-phone-volume"
                style={{ color: "#2BAE4A", fontSize: 17 }}
              />
            </a>
          </div>
        )}

        {d.transporterEmail && (
          <InfoTile
            icon="far fa-envelope"
            label="Transporter Email"
            value={show(d.transporterEmail)}
          />
        )}
      </div>

      {kind === "transport" && (
        <>
          <div
            onClick={() => {
              setDriverOpen((v) => !v);
              if (driverOpen) setMapOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            <span style={S.sectionTitle}>Driver &amp; Vehicle Information</span>
            <span
              style={{
                marginLeft: "auto",
                width: 24,
                height: 24,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: PURPLE,
              }}
            >
              <i
                className={
                  driverOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
                }
                style={{ fontSize: 11 }}
              />
            </span>
          </div>
          {!driverOpen ? null : tripLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skeleton height={76} />
              <Skeleton height={58} />
            </div>
          ) : !driver ? (
            <div style={S.note}>
              <i
                className="fas fa-info-circle"
                style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
              />
              <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
                No driver/vehicle assigned yet.
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  ...S.infoCard,
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 10,
                  padding: "11px 12px",
                }}
              >
                <div
                  className="ca-driver-header"
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    url={driver.driverPhotoUrl}
                    name={driver.driverName}
                    size={46}
                  />
                  <div
                    style={{
                      minWidth: 0,
                      flex: "1 1 auto",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: "#1c1c1e",
                      }}
                    >
                      {driver.driverName || "--"}
                    </div>
                    {driver.driverMobileNo && (
                      <a
                        href={`tel:${driver.driverMobileNo}`}
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: ORANGE,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <i
                          className="fas fa-phone-alt"
                          style={{ fontSize: 10 }}
                        />
                        {driver.driverMobileNo}
                      </a>
                    )}
                  </div>
                  <div
                    className="ca-driver-actions"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 7,
                        height: 30,
                        boxSizing: "border-box",
                        background: "#F7F5FE",
                        borderRadius: 8,
                        padding: "0 11px",
                      }}
                    >
                      <i
                        className="fas fa-car-side"
                        style={{ color: PURPLE, fontSize: 12 }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#1c1c1e",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {driver.vehicleNo || "--"}
                      </span>
                      {driver.vehicleType && (
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: GREY,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {driver.vehicleType}
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        height: 30,
                        boxSizing: "border-box",
                        borderRadius: 999,
                        padding: "0 11px",
                        fontSize: 11.5,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                        background: crewStatusStyle(
                          driver.isBoarded,
                          driver.isCompleted,
                          driver.isCancelled,
                        ).bg,
                        color: crewStatusStyle(
                          driver.isBoarded,
                          driver.isCompleted,
                          driver.isCancelled,
                        ).fg,
                      }}
                    >
                      <i
                        className={
                          crewStatusStyle(
                            driver.isBoarded,
                            driver.isCompleted,
                            driver.isCancelled,
                          ).icon
                        }
                        style={{ fontSize: 10 }}
                      />
                      {driver.tripStatus || "--"}
                    </span>
                    {(driver.isBoarded ||
                      driver.isCompleted ||
                      !driver.isCancelled) && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: 30,
                          boxSizing: "border-box",
                          borderRadius: 999,
                          padding: "0 10px",
                          fontSize: 11,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          background: "#EAEAEF",
                          color: GREY,
                        }}
                      >
                        {driver.isCompleted
                          ? `Completed ${driver.completedTime}`
                          : driver.isBoarded
                            ? `Boarded ${driver.boardedTime}`
                            : `PU ${driver.pickupTime}`}
                      </span>
                    )}
                    {driver.showTracking && (
                      <button
                        type="button"
                        onClick={() => {
                          // The GPS-polling effect (keyed on mapOpen) already fires an initial
                          // getTripTracking fetch itself via connectTrip — no need to call
                          // onLoadTripTracking here too (that would double-fetch on open).
                          setMapOpen(true);
                          if (handlers.onTrackVehicle)
                            handlers.onTrackVehicle(activity);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 7,
                          height: 34,
                          boxSizing: "border-box",
                          padding: "0 13px",
                          borderRadius: 8,
                          border: "none",
                          background: PURPLE,
                          color: "#fff",
                          fontFamily: "inherit",
                          fontSize: 12.5,
                          fontWeight: 800,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        <i
                          className="fas fa-location-arrow"
                          style={{ fontSize: 11 }}
                        />
                        Track
                      </button>
                    )}
                    <button
                      type="button"
                      title="Refresh driver & vehicle info"
                      disabled={driverRefreshing}
                      onClick={() =>
                        handlers.onRefreshTripDriver &&
                        handlers.onRefreshTripDriver(activity)
                      }
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 34,
                        height: 34,
                        boxSizing: "border-box",
                        borderRadius: 8,
                        border: `1px solid ${PURPLE}`,
                        background: "#fff",
                        color: PURPLE,
                        cursor: driverRefreshing ? "default" : "pointer",
                        opacity: driverRefreshing ? 0.6 : 1,
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className="fas fa-sync-alt"
                        style={{
                          fontSize: 12,
                          animation: driverRefreshing
                            ? "spin 0.8s linear infinite"
                            : "none",
                        }}
                      />
                    </button>
                  </div>
                </div>

                {mapOpen && (
                  <div
                    style={{
                      borderTop: "1px solid #f4f3f8",
                      paddingTop: 10,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        height: 260,
                        borderRadius: 10,
                        overflow: "hidden",
                        background: "#0c1014",
                        border: "1px solid #23282f",
                        zIndex: 0,
                      }}
                    >
                      {trackingLoading ? (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            color: GREY,
                          }}
                        >
                          <i
                            className="fas fa-sync-alt"
                            style={{
                              fontSize: 22,
                              color: PURPLE,
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                            Loading live location...
                          </span>
                        </div>
                      ) : trackHasGeo ? (
                        <div
                          ref={mapRef}
                          className="ca-track-map-dark"
                          style={{ position: "absolute", inset: 0 }}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 7,
                            color: GREY,
                          }}
                        >
                          <i
                            className="fas fa-map-marked-alt"
                            style={{ fontSize: 26, color: "#b8b6c4" }}
                          />
                          <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                            Geo location not available
                          </span>
                        </div>
                      )}
                      <span
                        onClick={() => setLiveTrackOpen(true)}
                        title="Open live tracking"
                        style={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          zIndex: 500,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 30,
                          height: 30,
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.95)",
                          color: "#3c3c43",
                          fontSize: 12,
                          cursor: "pointer",
                          boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
                        }}
                      >
                        <i className="fas fa-expand" style={{ fontSize: 12 }} />
                      </span>
                      <span
                        onClick={() => setMapOpen(false)}
                        title="Close map"
                        style={{
                          position: "absolute",
                          top: 46,
                          right: 10,
                          zIndex: 500,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          height: 30,
                          padding: "0 12px",
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.95)",
                          color: "#3c3c43",
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: "pointer",
                          boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
                        }}
                      >
                        <i className="fas fa-times" style={{ fontSize: 11 }} />
                        Close map
                      </span>
                      <span
                        onClick={() =>
                          !trackingLoading && connRef.current?.replot()
                        }
                        title="Refresh location"
                        style={{
                          position: "absolute",
                          top: 82,
                          right: 10,
                          zIndex: 500,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          height: 30,
                          padding: "0 12px",
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.95)",
                          color: "#3c3c43",
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: trackingLoading ? "default" : "pointer",
                          opacity: trackingLoading ? 0.6 : 1,
                          boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
                        }}
                      >
                        <i
                          className="fas fa-sync-alt"
                          style={{
                            fontSize: 11,
                            animation: trackingLoading
                              ? "spin 0.8s linear infinite"
                              : "none",
                          }}
                        />
                        Refresh
                      </span>
                      <div
                        style={{
                          position: "absolute",
                          top: 118,
                          right: 10,
                          zIndex: 500,
                          display: "flex",
                          alignItems: "center",
                          borderRadius: 999,
                          overflow: "hidden",
                          background: "rgba(255,255,255,0.95)",
                          boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
                        }}
                      >
                        <span
                          onClick={() => setFollow((f) => !f)}
                          title={follow ? "Following vehicle" : "Follow vehicle"}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            height: 30,
                            padding: "0 12px",
                            background: follow ? PURPLE : "transparent",
                            color: follow ? "#fff" : "#3c3c43",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          <i
                            className="fas fa-location-arrow"
                            style={{ fontSize: 11 }}
                          />
                          Follow
                        </span>
                        <div
                          style={{
                            width: 1,
                            alignSelf: "stretch",
                            background: "rgba(40,30,70,0.12)",
                          }}
                        />
                        <span
                          onClick={() => {
                            setFollow(false);
                            const fitPts =
                              routeRef.current?.points?.length > 1
                                ? routeRef.current.points
                                : null;
                            if (fitPts && mapObj.current) {
                              mapObj.current.fitBounds(fitPts, {
                                padding: [36, 36],
                              });
                            }
                          }}
                          title="Fit route"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            height: 30,
                            padding: "0 12px",
                            background: "transparent",
                            color: "#3c3c43",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          <i
                            className="fas fa-expand"
                            style={{ fontSize: 11 }}
                          />
                          Fit route
                        </span>
                      </div>
                      {(() => {
                        const hasRouteSteps =
                          routeRef.current?.steps?.length > 0;
                        const hasDrop = !!tracking?.destination;
                        return (
                          <div
                            style={{
                              position: "absolute",
                              top: 154,
                              right: 10,
                              zIndex: 500,
                              display: "flex",
                              alignItems: "center",
                              borderRadius: 999,
                              overflow: "hidden",
                              background: "rgba(255,255,255,0.95)",
                              boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
                            }}
                          >
                          {hasRouteSteps && (
                            <span
                              onClick={() =>
                                setMapPanel((m) =>
                                  m === "route" ? "eta" : "route",
                                )
                              }
                              title="Turn-by-turn route"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                height: 30,
                                padding: "0 12px",
                                background:
                                  mapPanel === "route" ? PURPLE : "transparent",
                                color: mapPanel === "route" ? "#fff" : "#3c3c43",
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="fas fa-directions"
                                style={{ fontSize: 11 }}
                              />
                              Route
                            </span>
                          )}
                          {tracking?.destination && (
                            <span
                              onClick={() =>
                                setMapPanel((m) =>
                                  m === "drop" ? "eta" : "drop",
                                )
                              }
                              title="Drop details"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                height: 30,
                                padding: "0 12px",
                                background:
                                  mapPanel === "drop" ? PURPLE : "transparent",
                                color: mapPanel === "drop" ? "#fff" : "#3c3c43",
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="fas fa-flag-checkered"
                                style={{ fontSize: 11 }}
                              />
                              {boarded ? "Drop" : "Pickup"}
                            </span>
                          )}
                          {(hasRouteSteps || hasDrop) && (
                            <div
                              style={{
                                width: 1,
                                alignSelf: "stretch",
                                background: "rgba(40,30,70,0.12)",
                              }}
                            />
                          )}
                          <span
                            onClick={() => {
                              const next = !voiceOn;
                              setVoiceOn(next);
                              spokenStepRef.current = -1;
                              if (!next) {
                                if (synth) synth.cancel();
                                return;
                              }
                              if (synth) {
                                const u = new SpeechSynthesisUtterance(
                                  `Voice guidance on. ${prog?.message || ""}`,
                                );
                                u.lang = "en-IN";
                                synth.cancel();
                                synth.speak(u);
                              }
                            }}
                            title={
                              synth
                                ? "Spoken trip updates"
                                : "No speech synthesis in this browser"
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 30,
                              height: 30,
                              background: voiceOn ? PURPLE : "transparent",
                              color: voiceOn ? "#fff" : "#3c3c43",
                              fontSize: 12,
                              cursor: synth ? "pointer" : "default",
                              opacity: synth ? 1 : 0.5,
                            }}
                          >
                            <i
                              className={`fas fa-volume-${voiceOn ? "up" : "mute"}`}
                            />
                          </span>
                          </div>
                        );
                      })()}
                      {trackHasGeo &&
                        (tracking?.eta || geomRef.current) &&
                        (mapPanel === "route" ? (
                          <RouteStepsPanel
                            steps={routeRef.current?.steps || []}
                            activeStep={activeStep}
                          />
                        ) : mapPanel === "drop" ? (
                          <DropInfoPanel
                            destination={tracking?.destination}
                            destKind={
                              tracking?.destination?.isAirport
                                ? "Airport"
                                : tracking?.destination?.isHotel
                                  ? "Hotel"
                                  : tracking?.destination?.isResidence
                                    ? "Residence"
                                    : tracking?.destination?.isGroundDuty
                                      ? "Ground duty"
                                      : "Destination"
                            }
                            boarded={boarded}
                            routeSummary={routeRef.current?.summary}
                            routeDistanceText={routeRef.current?.distanceText}
                            pickupLocation={
                              (tracking?.passengers || []).find(
                                (p) => p.isCurrentPassenger,
                              )?.pickupLocation
                            }
                            pickupTime={
                              (tracking?.passengers || []).find(
                                (p) => p.isCurrentPassenger,
                              )?.pickupTime
                            }
                          />
                        ) : (
                          <TripEtaDial
                            prog={prog}
                            gps={gps}
                            boarded={boarded}
                            routeSummary={routeRef.current?.summary}
                            wholeTripText={
                              tracking?.eta?.routeDistanceText ||
                              (geomRef.current
                                ? km(geomRef.current.total)
                                : null)
                            }
                          />
                        ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px 14px",
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: GREY,
                      }}
                    >
                      {[
                        ["Vehicle", PURPLE],
                        ["You", ORANGE],
                        ["Boarded", "#1e7a45"],
                        ["Pending pickup", "#c4c2ce"],
                        ["Destination", PURPLE_DARK],
                      ].map(([label, color]) => (
                        <span
                          key={label}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: color,
                            }}
                          />
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {liveTrackOpen && (
                  <div className="ca-live-panel">
                    <div className="ca-live-panel-header">
                      <span>
                        Live Trip Tracking
                        {driver?.vehicleNo ? ` · ${driver.vehicleNo}` : ""}
                      </span>
                      <button
                        type="button"
                        className="ca-live-panel-close"
                        aria-label="Close"
                        onClick={() => setLiveTrackOpen(false)}
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                    <div className="ca-live-panel-body">
                      <LiveTripTracking
                        key={liveTrackTransactionId}
                        airlineCode={liveTrackAirlineCode}
                        transactionId={liveTrackTransactionId}
                        employeeNumber={employeeNumber}
                        height="100%"
                        className="ca-live-panel-map"
                        onDeparted={() => setLiveTrackOpen(false)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <div style={{ ...S.infoCard, flexWrap: "wrap" }}>
                  <span
                    style={{
                      ...S.iconCircle,
                      background: "#EDE8FD",
                      color: PURPLE,
                    }}
                  >
                    <i className="fas fa-key" style={{ fontSize: 12 }} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.label}>Boarding OTP</div>
                    <div
                      style={{
                        fontSize: 16,
                        color: PURPLE,
                        fontWeight: 800,
                        letterSpacing: "0.1em",
                      }}
                    >
                      {driver.boardingOtp || "--"}
                    </div>
                  </div>
                  {Boolean(driver.boardingOtp) && (
                    <span
                      title={
                        driver.boardingOtpUsed
                          ? "Otp Verified"
                          : "Not Verified"
                      }
                      style={{
                        marginLeft: "auto",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        borderRadius: 10,
                        padding: "5px 10px",
                        fontSize: 11,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        background: verifiedStyle(driver.boardingOtpUsed).bg,
                        color: verifiedStyle(driver.boardingOtpUsed).fg,
                      }}
                    >
                      <i
                        className={verifiedStyle(driver.boardingOtpUsed).icon}
                        style={{ fontSize: 10, flexShrink: 0 }}
                      />
                      {driver.boardingOtpUsed
                        ? "Driver has verified this otp"
                        : "Share OTP with driver"}
                    </span>
                  )}
                </div>
                {eta && (
                  <InfoTile
                    icon="fas fa-stopwatch"
                    iconBg="#E6F4EA"
                    iconFg="#1e7a45"
                    label={eta.isBoarded ? "ETD" : "ETA"}
                    value={eta.trackingMessage || "--"}
                  />
                )}
                {eta && (
                  <InfoTile
                    icon="fas fa-route"
                    iconBg="#E6F4EA"
                    iconFg="#1e7a45"
                    label="Distance"
                    value={eta.distanceText || "--"}
                  />
                )}
                {eta?.location && (
                  <InfoTile
                    icon="fas fa-map-marker-alt"
                    iconBg="#E6F4EA"
                    iconFg="#1e7a45"
                    label="Vehicle's Current Location"
                    value={eta.location}
                  />
                )}
              </div>

              {driver.pickupLocation && (
                <div style={{ ...S.infoCard, alignItems: "flex-start" }}>
                  <span style={S.iconCircle}>
                    <i
                      className="fas fa-map-marker-alt"
                      style={{ fontSize: 13 }}
                    />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.label}>Driver pick-up location</div>
                    <div style={S.value}>{driver.pickupLocation}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            onClick={() => setGuardOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 2,
              cursor: "pointer",
            }}
          >
            <span style={S.sectionTitle}>Security Guard Information</span>
            <span
              style={{
                marginLeft: "auto",
                width: 24,
                height: 24,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: PURPLE,
              }}
            >
              <i
                className={
                  guardOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
                }
                style={{ fontSize: 11 }}
              />
            </span>
          </div>
          {!guardOpen ? null : guardLoading ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Skeleton height={62} style={{ flex: 1 }} />
              <Skeleton height={62} style={{ flex: 1 }} />
            </div>
          ) : !guard ? (
            <div style={S.note}>
              <i
                className="fas fa-info-circle"
                style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
              />
              <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
                No security guard assigned yet.
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <div style={{ ...S.infoCard, flex: "1 1 260px" }}>
                <Avatar
                  url={guard.securityGuardPhotoUrl}
                  name={guard.securityGuardName}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: "#1c1c1e",
                      fontWeight: 800,
                    }}
                  >
                    {guard.securityGuardName || "--"}
                  </div>
                  {guard.securityGuardMobileNo && (
                    <a
                      href={`tel:${guard.securityGuardMobileNo}`}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: ORANGE,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <i
                        className="fas fa-phone-alt"
                        style={{ fontSize: 10 }}
                      />
                      {guard.securityGuardMobileNo}
                    </a>
                  )}
                </div>
              </div>
              <div style={{ ...S.infoCard, flex: "1 1 180px" }}>
                <span
                  style={{
                    ...S.iconCircle,
                    background: "#EDE8FD",
                    color: PURPLE,
                  }}
                >
                  <i className="fas fa-key" style={{ fontSize: 12 }} />
                </span>
                <div>
                  <div style={S.label}>Security OTP</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      letterSpacing: "0.1em",
                      color: guard.securityGuardOtpUsed ? GREY : PURPLE,
                    }}
                  >
                    {guard.securityGuardOtp || "--"}
                  </div>
                </div>
                {Boolean(guard.securityGuardOtp) && (
                  <span
                    title={
                      guard.securityGuardOtpUsed
                        ? "Otp Verified"
                        : "Not Verified"
                    }
                    style={{
                      marginLeft: "auto",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      borderRadius: 10,
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      background: verifiedStyle(guard.securityGuardOtpUsed)
                        .bg,
                      color: verifiedStyle(guard.securityGuardOtpUsed).fg,
                    }}
                  >
                    <i
                      className={
                        verifiedStyle(guard.securityGuardOtpUsed).icon
                      }
                      style={{ fontSize: 10, flexShrink: 0 }}
                    />
                    {guard.securityGuardOtpUsed
                      ? "Guard has verified this otp"
                      : "Share OTP with security guard"}
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div
        onClick={() => setCrewOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <span style={S.sectionTitle}>Crew Members</span>
        {!tripLoading && crews && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: GREY }}>
            {crews.length} {crews.length === 1 ? "crew" : "crew members"}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            width: 24,
            height: 24,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: PURPLE,
          }}
        >
          <i
            className={
              crewOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
            }
            style={{ fontSize: 11 }}
          />
        </span>
      </div>
      {!crewOpen ? null : tripLoading || !crews ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {crews.map((c) => (
            <div
              key={`${c.srNo}-${c.employeeNumber}`}
              style={{
                ...S.infoCard,
                width: "100%",
                borderLeft: `3px solid ${PURPLE}`,
                padding: "10px 14px",
                gap: 12,
              }}
            >
              <Avatar url={c.crewProfilePhotoUrl} name={c.crewName} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{ fontSize: 13.5, color: "#1c1c1e", fontWeight: 800 }}
                >
                  {c.crewName}
                </div>
                <div style={{ fontSize: 11.5, color: GREY, fontWeight: 600 }}>
                  {(isShuttle
                    ? [c.airlineCrewType, c.employeeNumber]
                    : [c.airlineCrewType, c.employeeNumber, c.pickupLocation]
                  )
                    .filter(Boolean)
                    .join(" \u00b7 ")}
                </div>
                {c.crewMobileNo && (
                  <a
                    href={`tel:${c.crewMobileNo}`}
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: ORANGE,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <i className="fas fa-phone-alt" style={{ fontSize: 9 }} />
                    {c.crewMobileNo}
                  </a>
                )}
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  display: isShuttle ? "none" : "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 5,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 999,
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 800,
                    background: crewStatusStyle(
                      c.isBoarded,
                      c.isCompleted,
                      c.isCancelled,
                    ).bg,
                    color: crewStatusStyle(
                      c.isBoarded,
                      c.isCompleted,
                      c.isCancelled,
                    ).fg,
                  }}
                >
                  {c.tripStatus}
                </span>
                {(c.isBoarded || c.isCompleted || !c.isCancelled) && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      background: "#EAEAEF",
                      color: GREY,
                    }}
                  >
                    {c.isCompleted
                      ? `Completed ${c.completedTime}`
                      : c.isBoarded
                        ? `Boarded ${c.boardedTime}`
                        : `PU ${c.pickupTime}`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default TransportDetail;
